/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import * as tokenStore from "../services/tokenStore";
import {
  checkBiometricAvailability,
  authenticateWithBiometricsOrPin,
  signOut,
  // Kernel login (Slice A). signInWithPhone/signInWithEmail/verifyOtp are NOT imported
  // here any more — they are blaze's, and now serve only OtpPopup's contact-change resend.
  startKernelLogin,
  verifyKernelLogin,
  clearKernelLoginState,
  hasKernelLoginInFlight,
  authErrorMessage,
  registerDeviceWithBlaze,
  SEND_CODE_FAILED,
  fetchKernelMe,
  probeKernelSession,
  checkKernelSession,
  toE164,
} from "../services/auth";
import { clearUserScopedState, isSessionUser } from "../services/userScopedState";
import {
  exchangeSocialCode,
  openSocialLogin,
} from "../services/cognitoOauth";
import { getActionChecklist } from "../services/userChecklist";
import { UserPatient } from "./types";
import { useHistory } from "react-router";
import { clearReturnTo, takeReturnTo } from "../routes/returnTo";
import { NativeBiometric } from "capacitor-native-biometric";
import {
  processAndUploadHealthData,
  requestHealthKitPermissions,
} from "../services/healthkit";
import { Health } from "@capgo/capacitor-health";
import {
  getStoredPreferences,
  storePrefrences,
} from "../services/storageService";
import { clearLocalNotifications } from "../services/localNotifications";
import { resetAllBiometricSettings } from "../routes/BiometricAuthHandler";
import { set } from "date-fns";
import {
  PRIVACY_POLICY,
  TERMS_OF_USE,
  TERMS_OF_USE_VERSION,
} from "../utils/constants";
import { updateActionTracker } from "../services/userChecklist";
import { getProfileById, getUserPreferences } from "../services/user";
import crashlytics from "../services/crashlytics";
import { Device } from "@capacitor/device";
import analytics from "../services/firebaseAnalytics";
import { useStorage } from "./StorageContext";
import { SocialLogin } from "@capgo/capacitor-social-login";
import {
  clearPortalChoice,
  consumeFromPortalMarker,
  fetchKernelCapabilities,
  isStaffAccount,
  KernelCapabilities,
  resetPortalChoiceForNewSession,
} from "../services/staffPortal";
// Define the Auth state types
// Whitelist of non-PII fields safe to persist to local storage / Preferences.
// Everything else (email, phone, social accounts, PHI like condition/allergies)
// stays in React state only and is refetched from the server on cold start.
const PERSISTABLE_USER_KEYS = [
  "profile_id",
  "id",
  "first_name",
  "last_name",
  "name",
  "avatar",
  "hasSetBiometrics",
  "hasCompletedOnboarding",
  "socialProvider",
  "time_zone",
  "utc_offset",
  "username",
  "role",
] as const;

const sanitizeUserForStorage = (u: any): Record<string, any> => {
  if (!u) return {};
  const safe: Record<string, any> = {};
  for (const key of PERSISTABLE_USER_KEYS) {
    if (u[key] !== undefined && u[key] !== null) safe[key] = u[key];
  }
  return safe;
};

// Returns true if the stored user blob contains any field outside the whitelist
// (i.e. it was written by an older app build that persisted PII).
const hasLegacyPII = (u: any): boolean => {
  if (!u || typeof u !== "object") return false;
  const allowed = new Set<string>(PERSISTABLE_USER_KEYS as readonly string[]);
  return Object.keys(u).some((k) => !allowed.has(k));
};

// Web: Preferences is localStorage (XSS-readable) → strip PII before persist.
// Native: Preferences is OS-sandboxed → persist full user, unchanged.
const userForPreferences = (u: any): any =>
  Capacitor.getPlatform() === "web" ? sanitizeUserForStorage(u) : u;

export type User = UserPatient & {
  phone?: string;
  name?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  avatar?: string;
  hasSetBiometrics: boolean;
  hasCompletedOnboarding: boolean;
  profile_id: number;
  token?: string;
  socialProvider?: "google" | "facebook" | "apple" | "whatsapp" | null;
};

export type HeaderNotificationItem = {
  type: string;
  message?: string | number;
  allowClose?: boolean;
  schedule?: {
    time?: string;
  };
  style?: {
    bgColor: string;
    color: string;
  };
};

export type HeaderNotify = {
  data: Array<HeaderNotificationItem>;
};
type BiometricStatus = {
  isAvailable: boolean;
  biometricType: string | null;
};

// Add health-related types to the AuthContextType
type HealthDataStatus = {
  isAvailable: boolean;
  lastFetchTime: string | null;
  hasSleepData: boolean;
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  /**
   * False until the stored session has been read once at app start; true forever after,
   * whether or not anyone turned out to be signed in.
   *
   * ⚠️ This is NOT `isLoading`. `isLoading` also goes true for every sign-in, profile
   * refresh and logout, so gating the router on it blanks the screen mid-flow — which is
   * why the router's `if (isLoading) return null` guard was commented out and F-137 came
   * back. `user === null` is only trustworthy once this is true.
   */
  isSessionRestored: boolean;
  isAuthenticated: boolean;
  isVerifying: boolean;
  errorMessage: string | null;
  biometricStatus: BiometricStatus;
  isNewUser: boolean;
  consentsOutstanding: string[];
  /**
   * True when the signed-in account is provider staff (services/staffPortal.ts, R-753): it
   * gets the one-time StaffPortalPrompt and the "Switch to Provider Portal" menu item. False
   * for patients, while signed out, and whenever capabilities could not be read.
   */
  isStaff: boolean;
  isNativePlatform: boolean;
  platFormType: string | null;
  isUserHasDevice: boolean;
  healthDataStatus: HealthDataStatus;
  showOnboarding?: boolean;
  isDesktop?: boolean;
  handlesetShowOnboarding: (show: boolean) => Promise<void>;
  signInWithPhoneNumber: (countryCode: string, phone: string) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  signInWithSocial: (
    provider: "google" | "facebook" | "apple" | "whatsapp",
  ) => Promise<void>;
  completeSocialLogin: (code: string, state: string | null) => Promise<any>;
  signInWithBiometrics: () => Promise<boolean>;
  logout: () => Promise<void>;
  updateUserProfile: (userData: Partial<User>) => Promise<void>;
  setHasSetBiometrics: (hasSet: boolean) => Promise<void>;
  clearError: () => void;
  setAuthToken: (token: string, refreshToken: string) => Promise<void>;
  registerWithPhone: (
    countrycode: string,
    phone: string,
    name: string,
  ) => Promise<any>;
  registerWithEmail: (email: string) => Promise<void>;

  // Enhanced health data methods
  getHealthData: () => Promise<any | null>;
};

// Create the Auth Context with default values
export const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isSessionRestored: false,
  isAuthenticated: true,
  isVerifying: false,
  errorMessage: null,
  isNewUser: true,
  consentsOutstanding: [],
  isStaff: false,
  isNativePlatform: false,
  platFormType: null,
  isUserHasDevice: false,
  biometricStatus: { isAvailable: false, biometricType: null },
  healthDataStatus: {
    isAvailable: false,
    lastFetchTime: null,
    hasSleepData: false,
  },
  showOnboarding: false,
  isDesktop: false,
  handlesetShowOnboarding: async () => {},
  signInWithPhoneNumber: async () => {},
  verifyCode: async () => {},
  signInWithSocial: async () => {},
  completeSocialLogin: async () => {},
  signInWithBiometrics: async () => false,
  logout: async () => { },
  updateUserProfile: async () => { },
  setHasSetBiometrics: async () => { },
  clearError: () => { },
  setAuthToken: async () => { },
  registerWithPhone: async () => { },
  registerWithEmail: async () => { },
  getHealthData: async () => null,
});

// Auth Provider Component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const handlesetShowOnboarding = async (boolean: boolean): Promise<void> => {
    setShowOnboarding(boolean);
  };
  const [isDesktop, setIsDesktop] = useState(!Capacitor.isNativePlatform());
  //Check if user details present in P
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionRestored, setIsSessionRestored] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [phone, setPhoneNumber] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [biometricStatus, setBiometricStatus] = useState<BiometricStatus>({
    isAvailable: false,
    biometricType: null,
  });
  const [isNewUser, setIsNewUser] = useState(true);
  // Documents the kernel still requires this profile to accept (ADR-0011). Empty means
  // nothing outstanding. The kernel ENFORCES this server-side — every authenticated route
  // except a five-route allowlist returns 403 CONSENT_REQUIRED until it clears — so this is
  // the signal to render the gate, never the gate itself. UI lands with task 9.
  const [consentsOutstanding, setConsentsOutstanding] = useState<string[]>([]);
  // Provider staff (R-734/R-753): drives StaffPortalPrompt and the portal menu item.
  const [isStaff, setIsStaff] = useState(false);

  // Re-read consent state from the SERVER. Never guess it locally: the kernel is the
  // authority (it enforces the block), so the gate must open and close on its answer.
  const refreshConsentState = useCallback(async () => {
    try {
      const me = await fetchKernelMe();
      setConsentsOutstanding(me?.consents_outstanding ?? []);
    } catch (e) {
      console.error("could not refresh consent state", e);
    }
  }, []);

  useEffect(() => {
    // `auth:consents-accepted` — the user just accepted; confirm with the server.
    // `auth:consent-required` — a kernel call was refused with 403 CONSENT_REQUIRED, which
    // can happen mid-session if a new required document is published (the kernel bulk-clears
    // every profile's watermark on publish). Re-read so the gate appears without a restart.
    window.addEventListener("auth:consents-accepted", refreshConsentState);
    window.addEventListener("auth:consent-required", refreshConsentState);
    return () => {
      window.removeEventListener("auth:consents-accepted", refreshConsentState);
      window.removeEventListener("auth:consent-required", refreshConsentState);
    };
  }, [refreshConsentState]);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [platFormType, setPlatFormType] = useState<string | null>(null);
  const [isUserHasDevice, setIsUserHasDevice] = useState(true);
  const [healthDataStatus, setHealthDataStatus] = useState<HealthDataStatus>({
    isAvailable: false,
    lastFetchTime: null,
    hasSleepData: false,
  });
  const history = useHistory();
  const { storage, isStorageReady } = useStorage();

  // Register new user with phone number
  const registerWithPhone = async (
    countrycode: string,
    phone: string,
    name: string,
  ) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      // First, send verification code to phone
      // The kernel signals failure with an HTTP status; axios rejects non-2xx, so
      // reaching this line already means success. A `status === "ok"` body check here
      // would read `undefined` as failure on a perfectly good 200.
      const verificationResponse = await startKernelLogin(toE164(countrycode, phone));

      setPhoneNumber(phone);

      // Store temporary registration data
      await Preferences.set({
        key: "pendingRegistration",
        value: JSON.stringify({
          phone,
          name,
        }),
      });

      setIsVerifying(true);
      return verificationResponse;
    } catch (error: any) {
      // login_start is rate-limited (1/60s per identifier), so "Registration failed" was
      // both wrong and self-defeating: it reads as an account problem and invites the retry
      // that will 429 again.
      setErrorMessage(authErrorMessage(error, SEND_CODE_FAILED));
      // ⚠️ Was `throw error.response.data`, which was wrong twice over. Offline, the request
      // interceptor rejects with a plain `Error("No network connection")` -- no `.response`
      // -- so it threw a TypeError and buried the real cause. And even on a real HTTP
      // failure it discarded the status, leaving the only caller to fall back on
      // `error.message` and print axios's "Request failed with status code 429". Rethrow
      // the error itself: the status is the part callers actually need.
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Check biometric availability on component mount
  useEffect(() => {
    const checkBiometrics = async () => {
      try {
        const status = await checkBiometricAvailability();
        setBiometricStatus({
          isAvailable: status.isAvailable,
          biometricType: status.biometricType as string | null,
        });
      } catch (error) {
        console.error("Error checking biometric availability:", error);
      }
    };

    // const checkHealthKitStatus = async () => {
    //   if (Capacitor.getPlatform() === "ios") {
    //     try {
    //       const available = await checkPlatformAvailability();

    //       // Get last fetch time from storage
    //       const { value: lastFetchTime } = await Preferences.get({
    //         key: "healthKitLastFetch",
    //       });

    //       // Check if sleep data exists using @capgo/capacitor-health
    //       let hasSleepData = false;
    //       if (available) {
    //         try {
    //           const endDate = new Date();
    //           const startDate = new Date();
    //           startDate.setDate(endDate.getDate() - 30); // Look back 30 days

    //           const sleepData = await Health.readSamples({
    //             dataType: "sleep",
    //             startDate: startDate.toISOString(),
    //             endDate: endDate.toISOString(),
    //             limit: 1, // Only need 1 record to confirm
    //           });

    //           hasSleepData = !!(sleepData && sleepData.samples && sleepData.samples.length > 0);
    //         } catch (err) {
    //           console.error("Error checking sleep data:", err);
    //         }
    //       }

    //       setHealthDataStatus({
    //         isAvailable: available,
    //         lastFetchTime,
    //         hasSleepData,
    //       });
    //     } catch (err) {
    //       console.error("Error checking HealthKit status:", err);
    //     }
    //   }
    // };

    if (Capacitor.isNativePlatform()) {
      setIsNativePlatform(true);
      setPlatFormType(Capacitor.getPlatform());
      checkBiometrics();
    } else {
      setIsNativePlatform(false);
      setPlatFormType("web");
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setIsLoading(true);
        const isNative = Capacitor.getPlatform() !== 'web';

        // R-753: opened by the portal's "Switch to Patient App" (`?from=portal`)? That is the
        // person choosing this app: no staff pop-up this session. Strips the marker.
        await consumeFromPortalMarker();

        if (!isNative) {
          // On web, auth is cookie-based. If an old bearer token exists in
          // Preferences (pre-cookie deploy), clear it and force re-login so the
          // user gets a clean session instead of a 401 storm.
          const oldToken = await tokenStore.getAccessToken();
          if (oldToken) {
            await tokenStore.clearTokens();
            // F-635: everything per-user, not just `user` - nobody is signed in here.
            await clearUserScopedState();
            return;
          }
        }

        const [userData, storedToken] = await Promise.all([
          Preferences.get({ key: "user" }),
          // On web, authToken lives in an HttpOnly cookie sent automatically.
          // On mobile, restore it from Preferences for the in-memory token state.
          // tokenStore already returns null on web (HttpOnly cookie), so the platform
          // branch lives there rather than being repeated here.
          tokenStore.getAccessToken().then((value) => ({ value })),
        ]);

        // ── F-635: the SESSION says who is signed in, never the stored user ───────────
        // The app used to show whoever was left in `CapacitorStorage.user` and request their
        // data by id, whatever the session was (a portal switch as 450 ran as 388; a fresh
        // phone sign-in as 32 kept 388). Ask kernel /me first; nothing is shown until it has
        // answered (the router waits on isSessionRestored). `?from=portal` arrivals are page
        // loads, so they come through here too.
        const storedUser: User | null = userData.value ? (JSON.parse(userData.value) as User) : null;
        const session = storedUser || !isNative ? await checkKernelSession() : null;
        const trustStored =
          !!storedUser &&
          ((session?.status === "signed-in" && isSessionUser(storedUser, session.me)) ||
            // Native, kernel unreachable (offline): the stored user and the token pair were
            // written together on this device, so start as before; if the session has gone,
            // the 401 interceptor signs out on the first call.
            (isNative && session?.status === "unreachable"));

        if (!trustStored) {
          // No session, someone else's session, or no stored user: nothing per-user on this
          // device may be shown or used. Then load as the session's user, if there is one.
          // R-1188 R2: web with the kernel unreachable - nobody is shown, but nothing is wiped
          // either (it may well be the same person); the next start with an answer decides.
          if (session?.status !== "unreachable") {
            await clearUserScopedState();
          }
          if (session?.status === "signed-out") {
            await tokenStore.clearTokens();
          } else if (session?.status === "signed-in") {
            // ── Cookie-only restore (R-753, review R-748 finding 3) ─────────────────
            // The shared kernel cookie (web) or the stored token pair (native) is signed in:
            // restore from it instead of asking for a second OTP; any failure falls through
            // to the sign-in screen.
            await restoreFromKernelCookie(session.me);
          }
        }

        if (trustStored && storedUser) {
          const parsedUser = storedUser;
          setUser(parsedUser);
          setIsNewUser(false);

          // On web the persisted user is sanitized (no PII), so screens like
          // /settings/account would otherwise show only the name until the
          // user navigates somewhere that refetches. Hydrate in-memory state
          // from the server now that the session cookie is in scope.
          if (!isNative && parsedUser?.profile_id) {
            void refetchFullProfile(parsedUser.profile_id);
          }

          // Staff check (R-753). Not awaited: staff are no longer sent away, so nothing
          // waits on it. A failure means "not staff": no pop-up, no portal menu item.
          void fetchKernelCapabilities()
            .then((caps) => setIsStaff(isStaffAccount(caps)))
            .catch((e) => {
              console.error("session restore: capabilities unavailable; treating as not staff", e);
              setIsStaff(false);
            });
        }

        if (isNative && storedToken.value && session?.status !== "signed-out") {
          setToken(storedToken.value);
        }

        // ⚠️ Re-check consent on RESTORE, not just after a fresh login.
        //
        // consentsOutstanding was previously populated only by verifyCode, so a user who
        // restarted the app never learned they were consent-blocked. The kernel would 403
        // every kernel route while the app looked fine — the Dashboard is blaze data, which
        // has no consent guard — so the block presented as "some features silently don't
        // work" with nothing explaining why.
        //
        // It also matters mid-life: publishing a new required document bulk-clears EVERY
        // profile's watermark, so someone already signed in becomes blocked without doing
        // anything.
        if (trustStored) {
          void refreshConsentState();
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        setErrorMessage("Failed to restore authentication state");
      } finally {
        setIsLoading(false);
        // The session question is now ANSWERED — signed in or not, either way. The router
        // waits on this, so it has to be set on every exit from this function, including
        // the early `return` above and the catch. That is what `finally` buys us: miss it
        // on one path and the app hangs on the waiting state forever (F-137's opposite).
        setIsSessionRestored(true);
      }
    };
    const checkScreenSize = () => {
      const isDesktopScreen = Capacitor.getPlatform() === "web";
      setIsDesktop(isDesktopScreen);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    checkDeviceId();
    checkAuthStatus();
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  /**
   * Register this device with blaze when the PUSH TOKEN ARRIVES, not only at login.
   *
   * ⚠️ APNs/FCM registration is asynchronous. `establishSession` reads `fcmToken` once, at
   * sign-in — so on a fresh install the token lands AFTERWARDS, the registration call skips
   * its push branch, and nothing retries until the next sign-in. Measured on iOS: the
   * `profile_handsets` row was created and the `auth` row was NOT, because the token did not
   * exist yet. Android hid this because `fcmToken` was already in Preferences from an earlier
   * run — a stale-state false pass.
   *
   * `setFCMTokenFromNative` (App.tsx) already dispatches `fcm-token` when the token lands, so
   * this listens rather than polls. Registration is idempotent — blaze matches on
   * install_id + platform and updates in place — so re-running it is safe.
   */
  useEffect(() => {
    const profileId = user?.profile_id;
    if (!profileId) return;

    const onFcmToken = () => {
      const source =
        (user as any)?.socialProvider || localStorage.getItem("authMethod") || "email";
      void registerDeviceWithBlaze(profileId, source);
    };

    window.addEventListener("fcm-token", onFcmToken);
    return () => window.removeEventListener("fcm-token", onFcmToken);
  }, [user?.profile_id]);

  // Read by the auth:logout listener below, which is registered once and would otherwise see
  // only the first render's `user`.
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    let loggingOut = false;
    const handleAuthLogout = async () => {
      if (loggingOut) return;
      // ⚠️ Nobody is signed in and a sign-in is under way: there is no session to end, and
      // logout() would Preferences.clear() the in-flight login (session + identifier) and push
      // /signin — throwing the user off the code screen with a code that can no longer be
      // verified (prod 2026-10-27). A 401 here came from a stray call, not from this user.
      if (!userRef.current && (await hasKernelLoginInFlight())) return;
      loggingOut = true;
      await logout().finally(() => { loggingOut = false; });
    };
    window.addEventListener("auth:logout", handleAuthLogout);
    return () => window.removeEventListener("auth:logout", handleAuthLogout);
  }, []);

  // Analytics setup useEffect
  useEffect(() => {
    const syncUserToAnalytics = async () => {
      const deviceInfo = await Device.getId();
      const id = deviceInfo.identifier;
      if (user) {
        // ✅ AUTHENTICATED USER - Hash the ID
        const userId = user.email || user.phone || `user_${user.profile_id}`;

        // Crashlytics - HASHED ID ONLY
        await crashlytics.setUserId(id);
        // ❌ REMOVED: user_email, user_name (PHI)

        // ✅ SAFE: Behavioral/technical properties only
        await crashlytics.setCustomKey("user_type", "authenticated");
        await crashlytics.setCustomKey("platform", platFormType || "unknown");
        await crashlytics.setCustomKey(
          "has_onboarded",
          user.hasCompletedOnboarding,
        );
        await crashlytics.setCustomKey("has_biometrics", user.hasSetBiometrics);
        if (user.socialProvider) {
          await crashlytics.setCustomKey(
            "social_provider",
            user.socialProvider,
          );
        }

        await analytics.setUserId(id);
        await analytics.setUserProperties({
          user_type: "authenticated",
          platform: platFormType || "unknown",
          has_onboarded: String(user.hasCompletedOnboarding),
          has_biometrics: String(user.hasSetBiometrics),
          social_provider: user.socialProvider || "none",
          // ✅ Disable Google data sharing
          allow_google_signals: "false",
          allow_ad_personalization: "false",
        });

        console.log("📊 Analytics user identity set (hashed):", id);
      } else {
        // ✅ ANONYMOUS USER - Hashed device ID
        try {
          // Crashlytics
          await crashlytics.setUserId(id);
          await crashlytics.setCustomKey("user_type", "anonymous");
          await crashlytics.setCustomKey("platform", platFormType || "unknown");

          // Firebase Analytics
          await analytics.setUserId(id);
          await analytics.setUserProperties({
            user_type: "anonymous",
            platform: platFormType || "unknown",
            allow_google_signals: "false",
            allow_ad_personalization: "false",
          });

          console.log("📊 Analytics anonymous user set:", id);
        } catch (error) {
          console.error("Error getting device ID:", error);
        }
      }
    };

    syncUserToAnalytics();
  }, [user, platFormType]);

  // Refetch the full user profile (including PII) from the server and merge
  // it into in-memory state without persisting. Used on cold start after
  // restoring the sanitized user from Preferences so that screens which need
  // email/phone/condition (InvitedNotification, settings, etc.) work normally.
  const refetchFullProfile = async (profileId: number) => {
    try {
      const resp = await getProfileById(profileId);
      const profile = resp?.profile;
      if (!profile) return;
      setUser((prev: any) => {
        if (!prev) return prev;
        // F-635: never merge one person's profile into another's. This merge kept the stored
        // profile_id and took the fetched phone/name - the header showed one person while
        // every data call went out under the other's id.
        if (profile.profile_id != null && Number(profile.profile_id) !== Number(prev.profile_id)) {
          console.error("profile refetch: the session's profile is not the shown user; not merged");
          return prev;
        }
        return {
          ...prev,
          ...profile,
          ...(profile.condition || {}),
          // Preserve auth-flow fields owned by the client
          profile_id: prev.profile_id ?? profile.id,
          hasSetBiometrics: prev.hasSetBiometrics ?? false,
          hasCompletedOnboarding: prev.hasCompletedOnboarding ?? false,
          socialProvider: prev.socialProvider ?? null,
        };
      });
    } catch (err) {
      // Non-fatal — user can still use the app with the minimal cached fields.
      console.warn("Profile refetch on boot failed:", err);
    }
  };

  /**
   * WEB ONLY. Cookie-only restore (R-753, review R-748 finding 3): the kernel cookie is shared
   * with the portal (one host, Path=/), so a person switching over from the portal is already
   * signed in even though this app has no stored `user`. Rebuild the session from the SAME
   * legs a sign-in uses — kernel `/me` (probed quietly, see probeKernelSession) and the blaze
   * profile — plus capabilities for the staff flag. Returns false, with nothing set, on any
   * 401 or missing leg, and the router then shows the sign-in screen as before.
   */
  const restoreFromKernelCookie = async (knownMe?: any): Promise<boolean> => {
    const me = knownMe ?? (await probeKernelSession());
    if (!me?.id) return false;

    const [profileResult, capsResult] = await Promise.allSettled([
      getProfileById(),
      fetchKernelCapabilities(),
    ]);
    const profile = profileResult.status === "fulfilled" ? profileResult.value : null;
    const profileUser = profile?.profile ?? profile ?? null;
    if (!profileUser) {
      console.error(
        "cookie restore: blaze profile unavailable; showing sign-in",
        profileResult.status === "rejected" ? profileResult.reason : null,
      );
      return false;
    }
    // F-635: load as the SESSION's user only.
    if (profileUser.profile_id != null && !isSessionUser(profileUser, me)) {
      console.error("cookie restore: blaze profile is not the session's profile; showing sign-in");
      return false;
    }

    const restored: User = {
      ...profileUser,
      ...profileUser.condition,
      hasSetBiometrics: profileUser.hasSetBiometrics || false,
    };
    setConsentsOutstanding(me.consents_outstanding ?? []);
    setUser(restored);
    setIsNewUser(false);
    // Sanitized on web, like every other write of the user to Preferences.
    await Preferences.set({ key: "user", value: JSON.stringify(userForPreferences(restored)) });
    setIsStaff(capsResult.status === "fulfilled" && isStaffAccount(capsResult.value));
    return true;
  };

  const checkDeviceId = async () => {
    const { value: deviceId } = await Preferences.get({ key: "deviceId" });
    if (!deviceId) {
      const newDeviceId = Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase(); // Generate a 6-digit alphanumeric ID
      await Preferences.set({ key: "deviceId", value: newDeviceId });
    }
  };

  const setAuthToken = async (newToken: string, refreshToken: string) => {
    try {
      setToken(newToken);
      // On web, tokens are stored in HttpOnly cookies set by the server.
      // On mobile, persist to Preferences for session resume.
      await tokenStore.setTokens(newToken, refreshToken);
    } catch (error) {
      console.error("Error setting auth token:", error);
      setErrorMessage("Failed to set authentication token");
    }
  };

  // Handle phone number sign in
  const signInWithPhoneNumber = async (countryCode: string, phone: string) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      // Kernel login (Slice A). toE164 composes the identifier EXACTLY — the kernel's
      // lazy-link matches it with `==`, so a formatting difference silently creates a
      // second orphaned profile rather than failing (D15b).
      const response: any = await startKernelLogin(toE164(countryCode, phone));
      setPhoneNumber(phone);
      setIsVerifying(true);
      return response;
    } catch (error) {
      console.error("Error signing in with phone:", error);
      setErrorMessage(authErrorMessage(error, SEND_CODE_FAILED));
    } finally {
      setIsLoading(false);
    }
  };
  const updateUserAgreement = async ({
    consent_type = [TERMS_OF_USE, PRIVACY_POLICY],
    version = TERMS_OF_USE_VERSION,
    user,
  }: {
    consent_type?: string[];
    version?: string;
    user: User;
  }) => {
    try {
      if (!user) return;
      // Update terms of use agreement
      await updateActionTracker({
        action_type: "consent",
        status: "completed",
        consent_type: consent_type,
        version: version,
      });
    } catch (error) {
      console.error("Error updating user agreement:", error);
    }
  };
  // Register new user with email
  const registerWithEmail = async (email: string) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const response: any = await startKernelLogin(email);
      setIsVerifying(true);
      return response;
    } catch (error) {
      console.error("Error signing in with email:", error);
      setErrorMessage(authErrorMessage(error, SEND_CODE_FAILED));
      // ⚠️ MUST rethrow. Swallowing here made a FAILED start look like a successful one:
      // Login.tsx awaited this, saw no throw, stored authData and pushed /verify — where
      // the arrival guard found no kernel session in flight and bounced straight back to
      // Sign In. A rate-limited sign-in therefore cleared the field and said NOTHING.
      // AuthContext's own errorMessage is not the one Login.tsx renders; it keeps its own.
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Turn a freshly-issued kernel token pair into a live session.
   *
   * ⚠️ SHARED BY BOTH SIGN-IN PATHS — OTP verify and social callback. They differ only in
   * how the tokens are obtained; everything after that (store, fetch, validate, set user)
   * is identical, and forking it is how the two drift apart (B1). The social flow arrives
   * here from a deep link rather than a button press, so this must not touch OTP-specific
   * state such as the verification session or `isVerifying`.
   */
  const establishSession = async (
    tokens: { access_token: string; refresh_token?: string; new_user?: boolean },
    loginSource: string,
  ) => {
    // ⚠️ `refresh_token` is optional in the type because WEB gets it as an HttpOnly
    // cookie and the kernel blanks both body tokens there (D6). Native always carries
    // one. Same behaviour as before the extraction; tokenStore no-ops on web.
    await setAuthToken(tokens.access_token, tokens.refresh_token ?? "");

    // ── Post-login: ONE round-trip of latency, TWO required legs + one optional ──
    //
    // ⚠️ CORRECTED (D27 amendment). The first cut treated `/me` as the only required leg
    // and both blaze calls as enrichment. That was wrong, and Phase 2 proved it: without
    // `getProfileById` there is no `User`, and without a `User` there is no session — the
    // app silently returned to Sign In having just authenticated successfully, because the
    // failure happened inside a `.catch()` that was DESIGNED to swallow it. A required leg
    // wearing a tolerant coat is worse than no error handling at all.
    //
    //   REQUIRED  fetchKernelMe      profile_id + consents_outstanding (kernel-owned)
    //   REQUIRED  getProfileById     the User object — /me returns only 7 fields
    //   optional  getActionChecklist onboarding state; genuinely cosmetic if absent
    //   optional  fetchKernelCapabilities  staff check (R-734); on failure, stay here
    //
    // Still one round-trip: parallel, not sequential. Only the failure semantics differ.
    const [meResult, profileResult, checklistResult, capsResult] = await Promise.allSettled([
      fetchKernelMe(),
      getProfileById(),
      getActionChecklist(),
      fetchKernelCapabilities(),
    ]);

    const me = meResult.status === "fulfilled" ? meResult.value : null;
    const profile = profileResult.status === "fulfilled" ? profileResult.value : null;
    const checklist = checklistResult.status === "fulfilled" ? checklistResult.value : null;

    if (checklistResult.status === "rejected") {
      // The one leg we tolerate: log it and carry on with onboarding state unknown.
      console.error("post-login: checklist fetch failed", checklistResult.reason);
    }

    // `profile` may be `{ profile: {...} }` (blaze's envelope) or the object itself.
    const profileUser = profile?.profile ?? profile ?? null;

    // F-635: the blaze profile must be the session's profile - never someone else's.
    const profileMismatch =
      !!me?.id && !!profileUser && profileUser.profile_id != null && !isSessionUser(profileUser, me);

    if (!me?.id || !profileUser || profileMismatch) {
      // A REQUIRED leg failed. Clear the token rather than leave a half-session: a stored
      // token with no user is exactly the state that looks logged-in and is not.
      const which = !me?.id
        ? "kernel /me"
        : profileMismatch
          ? "blaze profile (not the session's)"
          : "blaze profile";
      console.error(
        `post-login: required leg failed (${which})`,
        meResult.status === "rejected" ? meResult.reason : profileResult.status === "rejected" ? profileResult.reason : null,
      );
      await tokenStore.clearTokens();
      await clearKernelLoginState();
      throw new Error("Signed in, but your profile could not be loaded. Please try again.");
    }

    // F-635: a sign-in REPLACES whoever was stored on this device. If the stored user is not
    // the one who just signed in (or there is none), clear every per-user key and cache
    // before anything reads them; the same person signing in again keeps their caches.
    const previous = (await Preferences.get({ key: "user" })).value;
    if (!previous || !isSessionUser(JSON.parse(previous), me)) {
      await clearUserScopedState();
    }

    // ── Staff (R-734, revised by R-753) ─────────────────────────────────────────
    // Staff now CHOOSE: the session proceeds as for anyone, and StaffPortalPrompt offers
    // the portal once. A capabilities failure is NOT a login failure; it means "not staff".
    let caps: KernelCapabilities | null = null;
    if (capsResult.status === "fulfilled") {
      caps = capsResult.value ?? null;
    } else {
      console.error("post-login: capabilities unavailable; treating as not staff", capsResult.reason);
    }
    // A fresh sign-in is a new session: an earlier "Stay" no longer applies.
    await resetPortalChoiceForNewSession();
    setIsStaff(isStaffAccount(caps));

    setConsentsOutstanding(me.consents_outstanding ?? []);

    // Restore what the auth port removed: the blaze `auth` row (push), the
    // `profile_handsets` row, and profile.utc_offset / time_zone. Fire-and-forget by
    // design — see registerDeviceWithBlaze. Shared by OTP and social, because it lives
    // in establishSession rather than in either caller.
    void registerDeviceWithBlaze(me.id, loginSource);

    const response: any = {
      // ⚠️ loginSource is load-bearing, not decoration: VerifyLogin gates the post-OTP
      // `/home` redirect on it (D20 records this as part of the login-response contract).
      // The kernel does not return it, so it is reconstructed here — omit it and every
      // successful sign-in falls through to the "Incorrect code" branch.
      loginSource: (await Preferences.get({ key: "authMethod" })).value || "email",
      newUser: tokens.new_user,
      user: profileUser,
      checklist,
    };

    if (response.user) {
      const userData = response.user;
      const User: User = {
        ...userData,
        ...userData.condition,
        hasSetBiometrics: userData.hasSetBiometrics || false,
      };
      handlesetShowOnboarding(
        response?.checklist?.hasCompletedOnboarding || false
      );
      setUser(User);
      localStorage.removeItem("token");
      localStorage.removeItem("phone");
      setIsVerifying(false);
      await Preferences.set({
        key: "user",
        value: JSON.stringify(User),
      });
      if (response.newUser) {
        setIsNewUser(true);
        // Agreed terms for new user
        updateUserAgreement({
          consent_type: [TERMS_OF_USE, PRIVACY_POLICY],
          version: TERMS_OF_USE_VERSION,
          user: User,
        });
      } else {
        setIsNewUser(false);
      }
    }
    return response;
  };

  // Verify phone verification code
  const verifyCode = async (code: string) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      if (!code) {
        throw new Error("Verification code is missing");
      }

      // ── Kernel verify (Slice A) ───────────────────────────────────────────
      // No `status === "ok"` gate: the kernel signals with HTTP status and axios rejects
      // non-2xx, so reaching the next line already means success. Checking a body field
      // that no longer exists would read `undefined` as failure on a good 200.
      // ⚠️ The key is `termsAgreed`, written by useTermsAgreement — NOT "termsAccepted".
      // Reading the wrong key here is silent: terms_accepted just goes to the kernel as
      // false on every first-time registration, and nothing errors.
      const termsAccepted =
        (await Preferences.get({ key: "termsAgreed" })).value === "true";
      const tokens = await verifyKernelLogin(code, termsAccepted);
      await clearKernelLoginState();
      // F-651: the code is accepted, so the phone number registerWithPhone parked is no longer
      // needed (nothing reads it); it must not outlive this sign-in.
      await Preferences.remove({ key: "pendingRegistration" });
      return await establishSession(
        tokens,
        (await Preferences.get({ key: "authMethod" })).value || "email",
      );
    } catch (error: any) {
      console.error("Error verifying code:", error);
      // ⚠️ RE-THROW. This catch previously swallowed the error and returned `undefined`,
      // so a caller awaiting verifyCode could not tell a failed sign-in from a successful
      // one and would proceed as though logged in. That is the half-session defect D27
      // names, and it was already live here before the kernel port.
      //
      // A 429 is rate limiting, not a bad code (/auth/login/start is 1/60s per identifier
      // and verify is throttled too); saying "invalid code" there sends people to re-enter
      // a code that was fine.
      setErrorMessage(authErrorMessage(error));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Start social sign-in.
   *
   * ⚠️ This NO LONGER RETURNS A SESSION, and callers must not await one. The kernel's
   * `/auth/social/callback` takes a Cognito authorization code + PKCE verifier, not a
   * provider SDK token, so the flow now leaves the app entirely: system browser -> Hosted UI
   * -> provider -> deep link back to `arcconnect://auth/callback`. `completeSocialLogin`
   * finishes it from App.tsx's appUrlOpen listener.
   *
   * The previous implementation obtained a Google/Facebook token natively and posted it to
   * blaze, which also meant a `checkUserExists` call on every social sign-in — an account
   * existence oracle of exactly the kind audit R-4 removed from the kernel. Both are gone.
   */
  const signInWithSocial = async (
    provider: "google" | "facebook" | "apple" | "whatsapp",
  ) => {
    try {
      setErrorMessage(null);
      await openSocialLogin(provider);
    } catch (error: any) {
      console.error(`Error starting ${provider} sign-in:`, error);
      setErrorMessage(error?.message || "Could not start sign-in. Please try again.");
      throw error;
    }
  };

  /**
   * Finish social sign-in from the deep-link callback. Shares `establishSession` with the
   * OTP path — the only difference is where the tokens came from.
   */
  const completeSocialLogin = async (code: string, state: string | null) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const result = await exchangeSocialCode(code, state);
      const response = await establishSession(result, result.providerCode);
      // F-137: social sign-in owes the same debt as OTP — return the screen that sent the
      // user to sign in, or /home when nothing was parked.
      history.push(takeReturnTo());
      return response;
    } catch (error: any) {
      console.error("Social sign-in could not be completed:", error);
      // TERMS_NOT_ACCEPTED is the kernel refusing a FIRST-TIME registration that never saw
      // the gate. It is not a failure of the provider, and saying so sends people to retry
      // the same thing forever.
      const code_ = error?.response?.data?.code;
      setErrorMessage(
        code_ === "TERMS_NOT_ACCEPTED"
          ? "Please accept the Terms of Use and Privacy Policy, then try again."
          : authErrorMessage(error, "Sign-in could not be completed. Please try again."),
      );
      history.push("/signin?register=false");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle biometric sign in
  const signInWithBiometrics = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      if (!biometricStatus.isAvailable) {
        throw new Error(
          "Biometric authentication is not available on this device",
        );
      }

      const success = await authenticateWithBiometricsOrPin();

      if (success) {
        // In a real-world app, you would validate with your backend here
        // For now, we'll just restore the user from storage if we have it
        const userData = await Preferences.get({ key: "user" });
        const storedToken = await tokenStore.getAccessToken();

        if (userData.value && storedToken) {
          setUser(JSON.parse(userData.value) as User);
          setToken(storedToken);
          return true;
        } else {
          throw new Error("No stored credentials found. Please sign in first.");
        }
      } else {
        throw new Error("Biometric authentication failed");
      }
    } catch (error: any) {
      console.error("Error signing in with biometrics:", error);
      setErrorMessage(`Biometric authentication failed: ${error.message}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    try {
      // Check if biometrics are available
      const { isAvailable, biometryType } = await NativeBiometric.isAvailable();
      console.log("Biometric availability check:", {
        isAvailable,
        biometryType,
      });

      // Get proper biometry name for user feedback
      const biometryName =
        biometryType === 2
          ? "Face ID"
          : biometryType === 1
            ? "Touch ID"
            : "Biometric";

      // If biometrics aren't available, go straight to device passcode
      if (!isAvailable) {
        console.log("Biometrics not available, using device passcode instead");
        try {
          await NativeBiometric.verifyIdentity({
            reason: "Please authenticate to continue",
            title: "Authentication Required",
            subtitle: "Use your device passcode to log in",
            useFallback: true, // Enable device passcode
          });

          // If we get here, authentication was successful
          console.log("Passcode authentication successful");
        } catch (error) {
          handleAuthError(error);
        }
        return;
      }

      // Biometrics are available, try biometric authentication

      try {
        await NativeBiometric.verifyIdentity({
          reason: "Please authenticate to continue",
          title: "Authentication Required",
          subtitle: `Use ${biometryName} to log in`,
          useFallback: true, // Enable fallback to device passcode if biometrics fail
        });

        // If we get here, authentication was successful
        history.push("/home");
      } catch (error) {
        handleAuthError(error);
      }
    } catch (error) {
      console.error("Unexpected error during authentication process:", error);
    }
  };

  const handleAuthError = (error: any) => {
    // Check for specific error codes
    if (error && typeof error === "object" && "code" in error) {
      const code = error.code;

      let errorMsg = "Authentication failed";
      if (code === 10) {
        // LAErrorAuthenticationFailed
        errorMsg = "Authentication failed. Please try again.";
      } else if (code === 11) {
        // LAErrorUserCancel
        errorMsg = "Authentication canceled.";
      } else if (code === 12) {
        // LAErrorUserFallback
        errorMsg = "Switched to device passcode.";
      } else if (code === 13) {
        // LAErrorSystemCancel
        errorMsg = "Authentication was canceled by the system.";
      } else if (code === 14) {
        // LAErrorPasscodeNotSet
        errorMsg =
          "Device passcode is not set, which is required for authentication.";
      } else if (code === 16) {
        // LAErrorBiometryNotAvailable
        errorMsg = "Biometric authentication is not completed try again.";
      } else {
        errorMsg = `Authentication error: ${code}`;
      }

      console.error("Error during authentication:", error);
    } else {
      console.error("Unknown error during authentication:", error);
    }
  };

  // Logout function
  //
  // ⚠️ ORDER IS LOAD-BEARING. `signOut()` runs FIRST, while the credentials still exist.
  // This block used to clear `tokenStore` and `Preferences` before calling it, which meant
  // the sign-out request went out UNAUTHENTICATED: the kernel 401s and revokes nothing, blaze
  // 401s and never nulls `push_notifications_token`. The device looked signed out while the
  // server session stayed live for its full TTL and push kept arriving — and nothing errored,
  // because both legs are deliberately tolerant. Do not reorder these.
  const logout = async () => {
    const fcmToken = await getStoredPreferences("fcmToken").catch(() => null);
    const socialProvider = user?.socialProvider;

    setIsLoading(true);
    try {
      // Revoke server-side (kernel session + Cognito refresh token) and de-register push.
      // Bounded internally, so a dead network cannot strand the user on a spinner.
      await signOut({ push_notifications_token: fcmToken });
    } catch (error) {
      // Never block a local sign-out on a remote one. A user who wants off this device
      // gets off it; the worst case is a session that expires on its own schedule.
      console.warn("server-side sign-out did not complete:", error);
    }

    try {
      // ⚠️ P1 TRAP: this blanket clear happens to remove the session tokens today only
      // because tokenStore still keeps them in Preferences. The moment P1 moves them to
      // secure storage, Preferences.clear() will silently stop clearing them and logout
      // will leave a live token on the device. tokenStore.clearTokens() is explicit and
      // survives that change — keep both until the blanket clear is narrowed.
      await tokenStore.clearTokens();
      await Preferences.clear();
      // F-635: and the cached per-user data store - the same set a change of user clears.
      await clearUserScopedState();
      if (fcmToken) {
        await storePrefrences("fcmToken", fcmToken);
      }
      setUser(null);
      setToken(null);
      // Drop any parked deep link. It belonged to the session that just ended; replaying
      // it at the NEXT sign-in would send a different person to someone else's screen.
      clearReturnTo();
      // ⚠️ Without this the user is TRAPPED on sign-out. ConsentGate's visibility is
      // `consentsOutstanding.length > 0` and the modal is deliberately non-dismissible —
      // logging out is documented as its only escape hatch. Leaving this array populated
      // keeps that modal mounted over the sign-in page, where every tap does nothing and
      // the only way out is force-stopping the app. Observed live on device 2026-08-31.
      setConsentsOutstanding([]);
      // R-753: the next account in this tab is judged on its own — no staff flag, and no
      // remembered "Stay in Patient App" (Preferences.clear above covers native).
      setIsStaff(false);
      await clearPortalChoice();
      await clearLocalNotifications();
      await resetAllBiometricSettings();
      history.push("/signin?register=false");
      if (socialProvider === "facebook") {
        await SocialLogin.logout({ provider: "facebook" });
      }
    } catch (error) {
      console.error("Error logging out:", error);
      setErrorMessage("Failed to log out. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Update user profile
  const updateUserProfile = async (userData: Partial<User>) => {
    try {
      if (!user) return;

      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);

      // Update user in storage
      await Preferences.set({
        key: "user",
        value: JSON.stringify(userForPreferences(updatedUser)),
      });
    } catch (error) {
      console.error("Error updating user profile:", error);
      setErrorMessage("Failed to update profile. Please try again.");
    }
  };

  // Set whether user has set up biometrics
  const setHasSetBiometrics = async (hasSet: boolean) => {
    try {
      if (!user) return;

      const updatedUser = { ...user, hasSetBiometrics: hasSet };
      setUser(updatedUser);

      // Update user in storage
      await Preferences.set({
        key: "user",
        value: JSON.stringify(userForPreferences(updatedUser)),
      });
    } catch (error) {
      console.error("Error updating biometrics status:", error);
      setErrorMessage("Failed to update biometrics status.");
    }
  };

  // Enhanced function to get health data from HealthKit
  const getHealthData = async (): Promise<any | null> => {
    try {
      // Check if we're on native platform
      if (!isNativePlatform) {
        console.log("Health data fetching is only supported on iOS or Android");
        return null;
      }

      // Check if user has completed health onboarding
      let accessHealthValue = (
        await Preferences.get({ key: "accessHealthKit" })
      ).value;

      if (!accessHealthValue) {
        // Local pref not set — fetch from backend to check if user has health enabled
        const response = await getUserPreferences();
        const healthEnabled = response?.data?.health_connect === true;
        accessHealthValue = healthEnabled ? "true" : "false";
        await Preferences.set({ key: "accessHealthKit", value: accessHealthValue });

        // If backend has health enabled, request permissions once
        if (healthEnabled) {
          await requestHealthKitPermissions();
        }
      }

      if (accessHealthValue !== "true") {
        console.log(
          "Health onboarding not completed, skipping health data sync",
        );
        return null;
      }

      // Fetch the health data with provided options or defaults
      // const healthData: any = await fetchHealthKitData();
      const healthData: any = await processAndUploadHealthData(storage);

      // Store the last fetch time
      const now = new Date().toISOString();
      await Preferences.set({
        key: "healthKitLastFetch",
        value: now,
      });

      // Update health data status
      setHealthDataStatus((prevStatus) => ({
        ...prevStatus,
        lastFetchTime: now,
        hasSleepData: !!(
          healthData.sleepAnalysis && healthData.sleepAnalysis.countReturn > 0
        ),
      }));

      return healthData;
    } catch (error) {
      console.error("Error fetching health data:", error);
      setErrorMessage("Failed to fetch health data");
      return null;
    }
  };

  // Clear any error messages
  const clearError = () => {
    setErrorMessage(null);
  };

  // Create the context value
  const value = {
    user,
    isLoading,
    isSessionRestored,
    isVerifying,
    isAuthenticated: !!user,
    errorMessage,
    biometricStatus,
    isNewUser,
    consentsOutstanding,
    isStaff,
    isNativePlatform,
    platFormType,
    isUserHasDevice,
    healthDataStatus,
    showOnboarding,
    isDesktop,
    handlesetShowOnboarding,
    signInWithPhoneNumber,
    verifyCode,
    signInWithSocial,
    completeSocialLogin,
    signInWithBiometrics,
    logout,
    updateUserProfile,
    setHasSetBiometrics,
    clearError,
    setAuthToken,
    registerWithPhone,
    registerWithEmail,
    getHealthData,
    handleBiometricLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
